import React, { useState } from "react";
import { X, Plus, Mail } from "lucide-react";
import { useGenerateReport } from "../../queries/admin";
import { toast } from "react-toastify";
import { format } from "date-fns";
import DatePicker from "./DatePicker";
import { HStack } from "../../components/component/utils";
import { Icon } from "@iconify/react";
import { useValueStore } from "../../store/useValueState";
import { IWeatherData } from "../../types/device";

// Export the metricDataMapping for use in other components
export const metricDataMapping: { [key: string]: keyof IWeatherData } = {
  Temperature: "temperature",
  Humidity: "humidity",
  Pressure: "pressure",
  "Carbon-Dioxide": "co2",
  VOCs: "vocs",
  Light: "light",
  Noise: "noise",
  PM1: "pm1",
  "PM2.5": "pm25",
  PM4: "pm4",
  PM10: "pm10",
  AIQ: "aiq",
  "Gas-1": "gas1",
  "Gas-2": "gas2",
  "Gas-3": "gas3",
  "Gas-4": "gas4",
  "Gas-5": "gas5",
  "Gas-6": "gas6",
};

// Predefined email list - you can move this to a config file or fetch from API
const availableEmails = [
  "admin@sensedata.com",
  "reports@sensedata.com",
  "manager@sensedata.com",
  "team@sensedata.com",
  "analytics@sensedata.com",
];

const ParameterSelectorReport: React.FC = () => {
  const { deviceId } = useValueStore();
  const { mutate: generateReport } = useGenerateReport();

  const [selectedFromDate, setSelectedFromDate] = useState<string>("");
  const [selectedToDate, setSelectedToDate] = useState<string>("");
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [newEmail, setNewEmail] = useState<string>("");

  const handleEmailSelect = (email: string) => {
    if (!selectedEmails.includes(email)) {
      setSelectedEmails([...selectedEmails, email]);
    }
  };

  const handleEmailRemove = (email: string) => {
    setSelectedEmails(selectedEmails.filter((e) => e !== email));
  };

  const handleAddNewEmail = () => {
    if (
      newEmail &&
      !selectedEmails.includes(newEmail) &&
      isValidEmail(newEmail)
    ) {
      setSelectedEmails([...selectedEmails, newEmail]);
      setNewEmail("");
    }
  };

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const filteredAvailableEmails = availableEmails.filter(
    (email) => !selectedEmails.includes(email),
  );

  return (
    <div className="max-w-8xl mx-auto w-full">
      <div className="p-4 mt-6">
        {/* Date Range Selection */}
        <div className="w-full flex flex-row gap-6 mb-8">
          <div className="w-full">
            <DatePicker
              label="From Date"
              value={selectedFromDate}
              onChange={setSelectedFromDate}
            />
          </div>
          <div className="w-full">
            <DatePicker
              label="To Date"
              value={selectedToDate}
              onChange={setSelectedToDate}
            />
          </div>
        </div>

        {/* Email Selection Section */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Select Email Recipients
          </h3>

          {/* Selected Emails */}
          <div className="mb-4">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
              Selected Recipients ({selectedEmails.length})
            </label>
            <div className="bg-gray-50 dark:bg-[#111B21] rounded-xl p-4 min-h-[80px] border border-gray-100 dark:border-gray-700">
              {selectedEmails.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {selectedEmails.map((email) => (
                    <span
                      key={email}
                      className="inline-flex items-center bg-primary text-white rounded-full px-3 py-1 text-sm font-semibold transition-all hover:bg-primary/90"
                    >
                      {email}
                      <button
                        onClick={() => handleEmailRemove(email)}
                        className="cursor-pointer ml-2 focus:outline-none hover:text-red-200"
                      >
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 dark:text-gray-400 text-sm italic flex items-center justify-center h-12">
                  No recipients selected. Choose from available emails below or
                  add a new one.
                </p>
              )}
            </div>
          </div>

          {/* Available Emails */}
          {filteredAvailableEmails.length > 0 && (
            <div className="mb-4">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                Available Email Addresses
              </label>
              <div className="bg-white dark:bg-[#111B21] rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex flex-wrap gap-2">
                  {filteredAvailableEmails.map((email) => (
                    <button
                      key={email}
                      onClick={() => handleEmailSelect(email)}
                      className="flex items-center bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full px-3 py-1 text-sm font-medium text-gray-700 dark:text-gray-300 transition-all hover:bg-gray-200 dark:hover:bg-gray-700"
                    >
                      <span>{email}</span>
                      <Plus size={14} className="ml-1.5 text-primary" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Add New Email */}
          <div className="mb-4">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
              Add New Email Address
            </label>
            <div className="flex gap-2">
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="Enter email address..."
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-primary dark:focus:ring-primary bg-white dark:bg-[#111B21] text-gray-900 dark:text-white"
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleAddNewEmail();
                  }
                }}
              />
              <button
                onClick={handleAddNewEmail}
                disabled={
                  !newEmail ||
                  !isValidEmail(newEmail) ||
                  selectedEmails.includes(newEmail)
                }
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                <Plus size={16} />
              </button>
            </div>
            {newEmail && !isValidEmail(newEmail) && (
              <p className="text-red-500 text-xs mt-1">
                Please enter a valid email address
              </p>
            )}
          </div>
        </div>

        {/* Generate Report Button */}
        <div className="space-y-6">
          <HStack className="justify-end">
            <button
              className={`w-fit flex items-center gap-2 py-3 px-6 rounded-xl font-medium transition-all ${
                selectedEmails.length > 0 && selectedFromDate && selectedToDate
                  ? "bg-primary hover:bg-primary/90 text-white shadow-md hover:shadow-lg"
                  : "bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
              }`}
              disabled={
                selectedEmails.length === 0 ||
                !selectedFromDate ||
                !selectedToDate
              }
              onClick={() => {
                generateReport(
                  {
                    deviceId: deviceId || "",
                    from: format(selectedFromDate, "yyyy-MM-dd"),
                    to: format(selectedToDate, "yyyy-MM-dd"),
                    email: selectedEmails.join(","),
                  },
                  {
                    onSuccess() {
                      toast(
                        `Report will be sent to ${selectedEmails.length} recipient(s)`,
                        {
                          type: "success",
                          autoClose: 3000,
                        },
                      );
                    },
                    onError() {
                      toast("Failed to generate report. Please try again.", {
                        type: "error",
                        autoClose: 2000,
                      });
                    },
                  },
                );
              }}
            >
              <Icon icon="lucide:download" className="w-4 h-4" />
              <span>Generate Report</span>
            </button>
          </HStack>
        </div>
      </div>
    </div>
  );
};

export default ParameterSelectorReport;
